import { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Camera, User, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaregiverProfilePhoto() {
  const { user, refreshUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);
  const fileInputRef = useRef(null);

  const handleCameraCapture = () => {
    // Check if camera API is available
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          // Camera permission granted — stop the test stream and proceed
          stream.getTracks().forEach(track => track.stop());
          fileInputRef.current?.click();
        })
        .catch((err) => {
          // Camera permission denied — show in-app message, never auto-redirect to Settings
          setCameraDenied(true);
        });
    } else {
      // Fallback for browsers without getUserMedia
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please capture a photo');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);

      await axios.post(`${API_URL}/api/users/profile-photo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Profile photo updated!');
      await refreshUser();
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="caregiver-profile-photo">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-muted border-2 border-border flex items-center justify-center overflow-hidden">
          {user?.profile_photo_url ? (
            <img src={user.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <User className="w-8 h-8 text-muted-foreground" />
          )}
        </div>
        <div>
          <Button variant="outline" size="sm"
            className="border-border text-foreground hover:bg-muted"
            onClick={handleCameraCapture}
            disabled={uploading}
            data-testid="take-photo-btn">
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Camera className="w-4 h-4 mr-2" />
            )}
            {uploading ? 'Uploading...' : 'Take Photo'}
          </Button>
          <p className="text-xs text-muted-foreground mt-1">Camera capture only</p>
        </div>
      </div>

      {/* Camera permission denied — in-app message with optional Settings button */}
      {cameraDenied && (
        <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="camera-denied-msg">
          <Camera className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-medium text-foreground">Camera access needed for profile photo</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              FendanaCura needs camera access to take your profile photo for identity verification.
              You can enable it in your browser or device settings.
            </p>
            <div className="flex gap-2 mt-2">
              <Button size="sm" variant="outline" className="text-[10px] h-6 px-2" data-testid="open-settings-btn"
                onClick={() => { if (window.navigator?.app?.exitApp) { window.open('app-settings:'); } else { window.open('about:blank'); } toast.info('Open your browser Settings > Privacy > Camera to enable access'); }}>
                Open Settings
              </Button>
              <Button size="sm" variant="ghost" className="text-[10px] h-6 px-2 text-muted-foreground"
                onClick={() => setCameraDenied(false)} data-testid="dismiss-camera-msg">
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Camera-only input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handleFileChange}
        className="hidden"
        data-testid="camera-input"
      />
    </div>
  );
}
